"use client";

/**
 * Impersonation engine — theme side-effect.
 *
 * Applies the firm's branding while impersonating roles whose registry entry declares
 * `theme: "firm"` (advisor / ria / enterprise_admin — they live inside a firm and should
 * see the app the way that user does). Other layers INHERIT the current theme; the
 * signed-in user's own firm theme is restored only on FULL exit (so an advisor
 * drilling into a client — Reviewer→Advisor→Client — keeps the firm theme).
 */
import { useCallback } from "react";
import useUpdateTheme from "@/shared/hooks/useUpdateTheme";
import { ROLE_REGISTRY } from "./registry";
import type { ImpersonationTarget } from "./types";

export interface ImpersonationTheme {
  /** Apply the firm theme for a target if its registry entry asks for it; else no-op. */
  applyFor: (target: ImpersonationTarget) => void;
  /**
   * Restore the signed-in user's own firm theme (falling back to the domain
   * theme when their firm has none) — call on full exit.
   */
  reset: () => void;
}

export function useImpersonationTheme(): ImpersonationTheme {
  const { updateThemeForFirm, updateThemeForUser } = useUpdateTheme();

  const applyFor = useCallback(
    (target: ImpersonationTarget): void => {
      const spec = ROLE_REGISTRY[target.kind];
      // `theme` is optional and only present on some registry entries, so narrow with
      // `in` before reading it.
      const theme = "theme" in spec ? spec.theme : undefined;
      if (theme === "firm" && "firmId" in target && target.firmId) {
        updateThemeForFirm(String(target.firmId));
      }
    },
    [updateThemeForFirm],
  );

  return { applyFor, reset: updateThemeForUser };
}
